import React, { useCallback, useState } from "react";
import ConvertToLocalTimezone, {
  PowerPickTimeZoneToLocal,
} from "/app/src/components/generic/formatting/dateTime";
import _ from "lodash";

import { reportRowService, reportService } from "/app/src/services";
import { buildParams } from "/app/src/helpers/params";
import { useTranslation } from "react-i18next";
import { Report, ReportColumn } from "/app/src/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { ColumnSort, createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import {
  convertStringToDecimal,
  convertStringToPercentage,
} from "/app/src/helpers";
import { useSearchParams } from "react-router-dom";
import useQueryParams from "/app/src/hooks/useQueryParams";

// Check for different formatting depending on data type
const Cell = ({
  value,
  type,
  baseTable,
}: {
  value: string;
  type: string;
  baseTable: string;
}) => {
  if (type === "DateTime") {
    if (["APICall", "Log", "Task"].includes(baseTable)) {
      return <ConvertToLocalTimezone date={value} />;
    }
    return <PowerPickTimeZoneToLocal date={value} />;
  } else if (type === "Percentage") {
    return <div>{convertStringToPercentage(value)}</div>;
  } else if (type === "Decimal") {
    return <div>{convertStringToDecimal(value)}</div>;
  } else {
    return <div>{value}</div>;
  }
};

/**
 * ReportTable component renders a table displaying report rows with sortable columns.
 * @param {Object} props - The component props.
 * @param {Report} props.report - The report object.
 * @param {ReportColumn[]} props.columns - Array of report columns.
 * @param {function} props.dateValues - Callback function to handle date values including cache_expiration, powerpick_timestamp, and variance_timestamp.
 * @returns {JSX.Element} A table component displaying the report data.
 */
export default function ReportTable({
  report,
  columns,
  dateValues: dataValues,
}: {
  report: Report;
  columns: ReportColumn[];
  dateValues: (values: {
    cache_expiration: string;
    powerpick_timestamp: string;
    variance_timestamp: string;
  }) => void;
}) {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<ReportColumn>();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );
  const [sort, setSort] = useSortUpgrade([
    { id: String(report.sortColumnId), desc: report.sortDescending },
  ]);
  const queryClient = useQueryClient();

  /**
   * Formats the column sort data.
   * @param {ColumnSort[]} sortData - Array of sort objects.
   * @returns {Object} Formatted sort data object.
   */
  function formatSortData(sortData: ColumnSort[]) {
    if (sortData.length) {
      return {
        sortDirection: sortData[0].desc,
        sortColumnId: sortData[0].id,
      };
    } else {
      return {
        sortColumnId: null,
      };
    }
  }
  const reportRowsQuery = useQuery({
    queryKey: ["reportRows", report.id, page, pageSize],
    queryFn: () =>
      reportRowService
        .getAll(report.id, buildParams({ limit: pageSize, page }))
        .then((data) => {
          // Convert embedded error payloads into thrown errors so React Query marks the query as error
          const toMessage = (v) =>
            Array.isArray(v)
              ? String(v[0])
              : typeof v === "string"
                ? v
                : v !== null
                  ? JSON.stringify(v)
                  : undefined;
          const embeddedMsg =
            toMessage(data?.errorMessage) ??
            toMessage(data?.error) ??
            toMessage(data?.errors);
          if (embeddedMsg) {
            throw new Error(embeddedMsg || "Unknown error");
          }
          dataValues({
            cache_expiration: data.cache_expiration,
            powerpick_timestamp: data.powerpick_timestamp,
            variance_timestamp: data.variance_timestamp,
          });
          return data;
        }),
    enabled: columns.length > 0,
    initialData: { report_rows: [] },
    keepPreviousData: true,
    // Disable retry to prevent retrying requests that fail due to validation errors
    // This ensures users see the error message immediately instead of a loading state
    retry: false,
  });
  const reportRowsCountQuery = useQuery({
    queryKey: ["reportRowsCount", report.id],
    queryFn: () =>
      reportRowService
        .getAll(report.id, buildParams({ countOnly: true }))
        .then((data) => {
          // Convert embedded error payloads into thrown errors so React Query marks the query as error
          const toMessage = (v) =>
            Array.isArray(v)
              ? String(v[0])
              : typeof v === "string"
                ? v
                : v !== null
                  ? JSON.stringify(v)
                  : undefined;
          const embeddedMsg =
            toMessage(data?.errorMessage) ??
            toMessage(data?.error) ??
            toMessage(data?.errors);
          if (embeddedMsg) {
            throw new Error(embeddedMsg || "Unknown error");
          }
          return data;
        }),
    enabled: columns.length > 0,
    initialData: { count: 0 },
    select: (data) => data.count,
    retry: false,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: reportRowsCountQuery.data,
    isFetching: reportRowsQuery.isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const { mutateAsync: updateReport } = useMutation({
    mutationFn: (data: {
      [key: string]: string | number | boolean | Date | undefined;
    }) => reportService.updateSingle(report.id, data).then(handlePromiseError),
    onSuccess: (data) => {
      queryClient.setQueryData(["report", report.id], data);
      reportRowsQuery.refetch();
    },
  });

  /**
   * Updates the sort state and triggers a report update.
   * @param {Function|Array} updater - A function to update the current sort, or the new sort value.
   * @returns {Array} The new sort state.
   */
  const updateSort = useCallback(
    (updater) => {
      setSort((oldSort) => {
        const newSortValue =
          updater instanceof Function ? updater(oldSort) : updater;
        updateReport(formatSortData(newSortValue));
        return newSortValue;
      });
    },
    [setSort, updateReport],
  );

  /**
   * Formats the report columns for displaying in a table.
   * @param {ReportColumn[]} columns - Array of report column objects.
   * @returns {Array} Formatted columns data for the table.
   */
  const formatColumns = useCallback(
    (columns: ReportColumn[]) => {
      // Create React Table column data
      columns.sort((a, b) => {
        if (a?.position && b?.position) {
          return Number(a.position) > Number(b.position) ? 1 : -1;
        } else {
          return 0;
        }
      });

      const formatted = _.chain(columns)
        .map((column) => {
          return columnHelper.accessor(column.id.toString(), {
            id: column.id.toString(),
            cell: (info) => {
              let type: string = column.filterType;
              if (column.type === "comparison") {
                if (column.filterType === "DateTime") {
                  type = "Number";
                } else if (column.filteringValue === "percentage") {
                  type = "Percentage";
                } else if (column.filteringValue === "ratio") {
                  type = "Decimal";
                }
              }
              return (
                <Cell
                  value={info.getValue() as string}
                  type={type}
                  baseTable={column.baseTable}
                />
              );
            },
            header: column.name,
            meta: {
              clickable: false,
            },
          });
        })
        .value();
      return formatted;
    },
    [columnHelper],
  );

  // If either query errors, render a lightweight fallback (omit Table to avoid internal row model errors)
  if (reportRowsQuery.isError || reportRowsCountQuery.isError) {
    const rowsErrMsg = (reportRowsQuery.error as Error | undefined)?.message;
    const countErrMsg = (reportRowsCountQuery.error as Error | undefined)
      ?.message;
    // Prefer the rows error message; fallback to count error message
    const errorDetail = rowsErrMsg || countErrMsg;

    return (
      <div className="p-4 border border-red-300 bg-red-50 rounded-md">
        <h3 className="text-error-interactive font-semibold mb-2">
          {t("translation:error", "Error")}
        </h3>
        <p className="text-error-interactive text-sm">
          {t(
            "translation:error_loading_report_rows_generic",
            "There was an error loading the report data.",
          )}
        </p>
        {errorDetail && (
          <p className="text-xs text-error-interactive mt-2 break-all">
            {t("translation:details")}: {errorDetail}
          </p>
        )}
      </div>
    );
  }

  return (
    <Table
      rows={reportRowsQuery.data.report_rows}
      tableColumns={formatColumns(columns)}
      length={reportRowsCountQuery.data}
      sort={sort}
      setSort={updateSort}
      paginationEnabled={{
        currentPage: page,
        pageSize,
        setPage: updatePage,
        setPageSize: updatePageSize,
      }}
      enableRowSelection
      emptyText={t("translation:no_rows")}
      loading={reportRowsQuery.isFetching || reportRowsCountQuery.isFetching}
    />
  );
}
